import type { FC } from 'react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  chakra,
  Collapse,
  Divider,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { printFormat } from 'iban';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import { Scope } from 'common/hooks/useAuthenticatedAction/types';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import DetailSidebar from 'components/DetailSidebar';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import SupportingDocumentPreview from 'components/SupportingDocumentPreview';
import TransferInformationRow from 'components/TransferInformationRow';
import { formatDate, PPP } from 'helpers/date';
import { getBankTransferStatusDate } from 'helpers/teamMembersRequests/bankTransfer';

import { RejectRequestDocument } from '../../graphql/rejectRequest.gql';
import { useApproveBankTransferRequest } from '../../hooks/useApproveBankTransferRequest';
import { useBankTransferRecipientIbanSearch } from '../../hooks/useBankTransferRecipientIbanSearch';
import { useBankTransferRequestDetail } from '../../hooks/useBankTransferRequestDetail';
import RequestStatusCallout from '../RequestStatusCallout';
import { StrongAuthDetailSidebar } from '../StrongAuthDetailSidebar';
import DetailSidebarApproved from './components/DetailSidebarApproved';
import DetailSidebarRejected from './components/DetailSidebarRejected';
import locales from './locales';
import { ApproveFormRequestSchema, searchSchema } from './schema';

interface BankTransferRequestSidebarProps {
  onClose?: () => void;
  companyProfileId: string;
  requestId: string;
  isOnPendingFilter: boolean;
}

const APPROVED_REQUEST_STATUSES = [
  TeamMembersRequestStatus.Approved,
  TeamMembersRequestStatus.Completed,
  TeamMembersRequestStatus.Processed,
];

const PROCESSING_REQUEST_STATUSES = [
  TeamMembersRequestStatus.Approved,
  TeamMembersRequestStatus.Processed,
];

const BankTransferRequestSidebar: FC<BankTransferRequestSidebarProps> = ({
  companyProfileId,
  isOnPendingFilter,
  onClose,
  requestId,
}) => {
  const form = useForm<ApproveFormRequestSchema>({
    defaultValues: {
      shouldNotifyRecipient: false,
    },
    mode: 'onChange',
    resolver: zodResolver(searchSchema),
  });
  const shouldNotifyRecipient = form.watch('shouldNotifyRecipient');

  useEffect(() => {
    if (!shouldNotifyRecipient) {
      form.unregister('recipientEmail');
    }
  }, [form, shouldNotifyRecipient]);

  const {
    additionalSepaTransferFees,
    bankTransferRequest,
    error,
    hasExceededQuota,
    loading,
    nextBalance,
  } = useBankTransferRequestDetail({
    requestId,
  });
  const {
    bankTransferRecipient,
    error: errorRecipient,
    loading: loadingRecipient,
  } = useBankTransferRecipientIbanSearch(
    bankTransferRequest?.bankTransferRequesterPayload.bankTransferRecipient
      .iban,
  );

  const [approveBankTransfer, { loading: loadingApproval }] =
    useApproveBankTransferRequest();
  const [rejectRequest, { loading: loadingRejectRequest }] =
    useErrorHandledMutation(RejectRequestDocument);

  if (error || errorRecipient) {
    return <ErrorCard />;
  }

  if (loading || loadingApproval || loadingRecipient || !bankTransferRequest) {
    return (
      <DetailSidebar variant="ghost">
        <DetailSidebar.Header />
        <DetailSidebar.Body>
          <Loader />
        </DetailSidebar.Body>
      </DetailSidebar>
    );
  }

  const canMakeTransfer =
    nextBalance >=
    bankTransferRequest.bankTransferRequesterPayload.amount +
      (hasExceededQuota ? additionalSepaTransferFees : 0);

  const shouldShowStatusRow = !PROCESSING_REQUEST_STATUSES.includes(
    bankTransferRequest.status,
  );

  const handleApproval = async (token: string) => {
    if (!canMakeTransfer) {
      return;
    }
    const values = form.getValues();
    await approveBankTransfer({
      context: {
        headers: {
          'short-lived-token': token,
        },
      },
      variables: {
        input: {
          bankAccountId:
            bankTransferRequest.bankTransferRequesterPayload.bankAccountId,
          companyProfileId,
          recipientEmail: values.recipientEmail,
          shouldNotifyRecipient: values.shouldNotifyRecipient,
          teamMembersRequestId: requestId,
        },
      },
    });
  };

  const amount = formatAmount({
    forcePlusSign: false,
    value: bankTransferRequest.bankTransferRequesterPayload.amount / 100,
    withNoMinorUnitDigits: true,
  });
  const recipient = `${
    bankTransferRecipient?.name ??
    bankTransferRequest.bankTransferRequesterPayload.bankTransferRecipient.name
  }
${printFormat(
  bankTransferRecipient?.iban ??
    bankTransferRequest.bankTransferRequesterPayload.bankTransferRecipient.iban,
)}
  `;

  const employeeName = `${bankTransferRequest.requester?.firstName ?? ''} ${
    bankTransferRequest.requester?.lastName ?? ''
  }`;

  const formattedFees = formatAmount({
    forcePlusSign: false,
    value: hasExceededQuota ? additionalSepaTransferFees / 100 : 0,
  });

  if (
    isOnPendingFilter &&
    bankTransferRequest.status === TeamMembersRequestStatus.Rejected
  ) {
    return (
      <DetailSidebarRejected employeeName={employeeName} onClose={onClose} />
    );
  }

  if (
    isOnPendingFilter &&
    APPROVED_REQUEST_STATUSES.includes(bankTransferRequest.status)
  ) {
    return (
      <DetailSidebarApproved
        employeeName={employeeName}
        onClose={onClose}
        receiverName={
          bankTransferRecipient?.name ??
          bankTransferRequest.bankTransferRequesterPayload.bankTransferRecipient
            .name
        }
      />
    );
  }

  return (
    <StrongAuthDetailSidebar
      colorScheme="grey"
      description={locales.strongAuthSidebarDescription}
      onClose={onClose}
      onPasscodeSuccess={handleApproval}
      scope={Scope.TEAM_MEMBERS_REQUESTS_REVIEW}
    >
      {({ showPasscode }) => (
        <>
          <chakra.form
            display="contents"
            onSubmit={form.handleSubmit(() => {
              if (!canMakeTransfer) {
                return;
              }
              showPasscode();
            })}
          >
            <StrongAuthDetailSidebar.Header
              heading={amount}
              iconName="transaction-out"
              label={locales.transactionOut}
            />
            <StrongAuthDetailSidebar.Body
              alignItems="center"
              paddingX="space-0"
            >
              <SunshineTag
                colorScheme="grey"
                icon="profile"
                justifySelf="flex-end"
                size="sm"
              >
                {employeeName}
              </SunshineTag>
              <VStack
                alignItems="flex-start"
                marginY="space-32"
                paddingX="space-24"
                spacing="space-0"
              >
                {!isOnPendingFilter ? (
                  <RequestStatusCallout request={bankTransferRequest} />
                ) : null}
                <SupportingDocumentPreview
                  href={
                    bankTransferRequest.supportingDocumentsUrls[0] as string
                  }
                />
                <SunshineCard.Group isBoxed={false} marginTop="space-16">
                  <TransferInformationRow
                    label={locales.amountLabel}
                    value={amount}
                  />
                  {shouldShowStatusRow ? (
                    <TransferInformationRow
                      label={
                        locales.executionDateLabel[bankTransferRequest.status]
                      }
                      value={formatDate(
                        getBankTransferStatusDate(bankTransferRequest),
                        PPP,
                      )}
                    />
                  ) : null}
                  {bankTransferRequest.bankTransferRequesterPayload
                    .userLabel ? (
                    <TransferInformationRow
                      label={locales.userLabel}
                      value={
                        bankTransferRequest.bankTransferRequesterPayload
                          .userLabel
                      }
                    />
                  ) : null}
                  <TransferInformationRow
                    label={locales.recipientLabel}
                    value={recipient}
                  />
                </SunshineCard.Group>
                {isOnPendingFilter && !bankTransferRecipient ? (
                  <Callout hideIcon variant="info">
                    <Callout.Title>
                      {locales.newBankTransferRecipient}
                    </Callout.Title>
                  </Callout>
                ) : null}
              </VStack>
              {isOnPendingFilter ? (
                <>
                  <Divider size="lg" />
                  <VStack alignItems="flex-start" spacing="space-16">
                    <Controller
                      control={form.control}
                      name="shouldNotifyRecipient"
                      render={({ field: { name, onChange, value } }) => (
                        <SunshineCard
                          id={name}
                          isBoxed={false}
                          isChecked={value}
                          onChange={onChange}
                          padding="space-24"
                          title={locales.sendConfirmationEmailTitle}
                          variant="checkbox"
                        >
                          <Text paddingTop="space-8" size="sm">
                            {locales.sendConfirmationEmailLabel}
                          </Text>
                        </SunshineCard>
                      )}
                    />
                    <Box
                      paddingBottom="space-24"
                      paddingX="space-24"
                      width="100%"
                    >
                      <Collapse in={shouldNotifyRecipient} unmountOnExit>
                        <Form.Field
                          error={form.formState.errors.recipientEmail?.message}
                          label={locales.emailLabel}
                        >
                          <Form.Input
                            type="email"
                            {...form.register('recipientEmail')}
                          />
                        </Form.Field>
                      </Collapse>
                    </Box>
                  </VStack>
                </>
              ) : null}
            </StrongAuthDetailSidebar.Body>
            {isOnPendingFilter ? (
              <StrongAuthDetailSidebar.Footer gap="space-24">
                {hasExceededQuota ? (
                  <VStack alignItems="flex-start">
                    <Text fontWeight="weight-500" variant="primary">
                      {locales.exceededQuotaTitle}
                    </Text>
                    <Text>
                      {locales.formatString(locales.exceededQuotaDescription, {
                        fees: formattedFees,
                      })}
                    </Text>
                  </VStack>
                ) : null}
                {!canMakeTransfer ? (
                  <Callout hideIcon variant="error">
                    <Callout.Content>
                      {locales.insufficientFunds}
                    </Callout.Content>
                  </Callout>
                ) : null}
                <HStack spacing="space-24">
                  <Button
                    flex={1}
                    isLoading={loadingRejectRequest}
                    onClick={() =>
                      rejectRequest({
                        variables: {
                          input: {
                            companyProfileId,
                            teamMembersRequestId:
                              bankTransferRequest.teamMembersRequestId,
                          },
                        },
                      })
                    }
                    variant="secondary"
                  >
                    {locales.refuseRequest}
                  </Button>
                  <Button
                    flex={1}
                    isDisabled={!canMakeTransfer || loadingRejectRequest}
                    type="submit"
                    variant="primary"
                  >
                    {locales.approveRequest}
                  </Button>
                </HStack>
              </StrongAuthDetailSidebar.Footer>
            ) : null}
          </chakra.form>
        </>
      )}
    </StrongAuthDetailSidebar>
  );
};

export default BankTransferRequestSidebar;
